import classnames from 'classnames';
import capitalize from 'lodash/capitalize';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import React from 'react';

import Date from '../../../common/date';
import Money from '../../../common/money';
import Time from '../../../common/Time';

const column = (name, overrides = {}) => ({
  Header: capitalize(name),
  accessor: name,
  id: name,
  ...overrides,
});

const paymentLabels = {
  pos: 'POS',
  gift_card: 'Gift Card',
  cash: 'Cash/Other',
  squire: 'Via Squire',
  unpaid: 'Unpaid',
};

const AppointmentsReport = ({ appointments, filtersDirty, showBarberName, showShopName, showDepositsSecured }) => (
  <div className={classnames({ 'u-dimmed': filtersDirty })}>
    <div display-if={!appointments.length}>No data for selected date range and filters</div>
    <ReactTable
      display-if={appointments.length}
      data={appointments}
      showPagination
      defaultPageSize={500}
      pageSizeOptions={[5, 10, 25, 50, 100, 500, 1000]}
      minRows={0}
      className="c-table c-table-new c-table-with-sticky-header c-table--small c-no-hover u-mt--"
      columns={[
        ...(showShopName ? [column('shopName', { Header: 'Shop' })] : []),
        {
          Header: 'Due Date',
          accessor: 'dueDateTime',
          id: 'dueDate',
          Cell: props => <Date>{props.value}</Date>,
        },
        {
          Header: 'Due Time',
          accessor: 'dueDateTime',
          id: 'dueTime',
          Cell: props => <Time>{props.value}</Time>,
        },
        {
          Header: 'Created At',
          accessor: 'createdAt',
          id: 'createdAtDate',
          Cell: props => <Date>{props.value}</Date>,
        },
        {
          Header: 'Created Time',
          accessor: 'createdAt',
          id: 'createdAtTime',
          Cell: props => <Time>{props.value}</Time>,
        },
        {
          Header: 'Modified At',
          accessor: 'modifiedAt',
          id: 'modifiedAtDate',
          Cell: props => (props.value ? <Date>{props.value}</Date> : <span>n/a</span>),
        },
        {
          Header: 'Modified Time',
          accessor: 'modifiedAt',
          id: 'modifiedAtTime',
          Cell: props => (props.value ? <Time>{props.value}</Time> : <span>n/a</span>),
        },
        {
          Header: 'Modified By',
          accessor: 'modifiedBy',
          id: 'modifiedBy',
          Cell: props => <span>{props.value || 'n/a'}</span>,
        },
        {
          Header: 'Requested Professional',
          accessor: 'bookedWithAnyBarber',
          id: 'bookedWithAnyBarber',
          Cell: props => <span>{props.value ? 'No' : 'Yes'}</span>,
        },
        ...(showBarberName
          ? [
              {
                Header: 'Barber',
                accessor: 'barberFirstName',
                id: 'barber',
                Cell: props => (
                  <span>
                    {props.original.barberFirstName} {props.original.barberLastName}
                  </span>
                ),
              },
            ]
          : []),
        {
          Header: 'Customer',
          accessor: 'customerFirstName',
          id: 'customer',
          Cell: props => (
            <span>
              {props.original.customerFirstName} {props.original.customerLastName}
            </span>
          ),
        },
        column('email'),
        column('phone'),
        column('service'),
        {
          Header: 'Service cost',
          accessor: 'serviceCost',
          id: 'serviceCost',
          Cell: props => <Money cents={props.value} />,
        },
        ...(showDepositsSecured
          ? [
              {
                Header: 'Booking Deposits',
                accessor: 'depositsSecured',
                id: 'depositsSecured',
                Cell: props => <Money cents={props.value} />,
              },
            ]
          : []),
        {
          Header: 'Tip',
          accessor: 'tip',
          id: 'tipAmount',
          Cell: props => <Money cents={props.value} />,
        },
        {
          Header: 'Payment',
          accessor: 'payment',
          id: 'payment',
          Cell: props => <span>{props.value.map(payment => paymentLabels[payment]).join(', ')}</span>,
        },
        column('bookingType', { Header: 'Booking type' }),
        column('status'),
        column('groupId', {
          Header: 'Group ID',
          Cell: props => <span>{props.value ? props.value : 'n/a'}</span>,
        }),
        column('appointmentRating', {
          Header: 'Rating',
          Cell: props => <span>{props.value ? props.value : 'n/a'}</span>,
        }),
      ]}
      getTrProps={(state, rowInfo) => {
        return {
          'data-automation-id': `dataRow:appointment:${rowInfo.index}`,
        };
      }}
      getTdProps={(state, rowInfo, column) => {
        return {
          'data-automation-id': column.id ? `text:${column.id}` : null,
        };
      }}
    />
  </div>
);

AppointmentsReport.propTypes = {
  appointments: PropTypes.array.isRequired,
  filtersDirty: PropTypes.bool.isRequired,
  showBarberName: PropTypes.bool.isRequired,
  showShopName: PropTypes.bool.isRequired,
  showDepositsSecured: PropTypes.bool.isRequired,
};

export default AppointmentsReport;
